import { useFetchAssignmentChangeLogs } from "@/queries/useFetchAssignmentChangeLogs";
import {
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
} from "@mui/material";
import { Download, X } from "lucide-react";
import type React from "react";

interface AssignmentChangeLogModalProps {
    open: boolean;
    onClose: () => void;
    caseNumber: string;
    caseUUID: string;
}

const AssignmentChangeLogModal: React.FC<AssignmentChangeLogModalProps> = ({
    open,
    onClose,
    caseNumber,
    caseUUID,
}) => {
    const { data: changeLogs, isLoading, isError } = useFetchAssignmentChangeLogs(caseUUID);

    const handleExportCSV = () => {
        if (!changeLogs || changeLogs.length === 0) {
            return;
        }

        const headers = [
            "Case Number",
            "Field Name",
            "Previous Value",
            "New Value",
            "Changed By",
            "Changed At",
        ];
        const csvRows = [headers.join(",")];

        changeLogs.forEach((log) => {
            const row = [
                caseNumber,
                `"${log.fieldDisplayName}"`,
                `"${log.previousValue || "None"}"`,
                `"${log.newValue}"`,
                `"${log.changedBy}"`,
                new Date(log.createdAt).toLocaleString(),
            ];
            csvRows.push(row.join(","));
        });

        const csvContent = csvRows.join("\n");
        const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = `Assignment_Changes_${caseNumber}_${
            new Date().toISOString().split("T")[0]
        }.csv`;
        link.click();
        URL.revokeObjectURL(url);
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="md"
            fullWidth
            sx={{
                "& .MuiDialog-paper": {
                    maxWidth: 900,
                    width: "100%",
                    maxHeight: "80vh",
                },
            }}
        >
            <DialogTitle>
                <Box sx={{ pr: 6 }}>
                    <Typography variant="h6" component="div">
                        Assignment Change Log
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                        Case {caseNumber}
                    </Typography>
                </Box>
                <IconButton
                    onClick={onClose}
                    size="small"
                    sx={{
                        position: "absolute",
                        right: 8,
                        top: 8,
                        color: (theme) => theme.palette.grey[500],
                    }}
                >
                    <X size={20} />
                </IconButton>
            </DialogTitle>

            <DialogContent dividers>
                {isLoading ? (
                    <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
                        <CircularProgress />
                    </Box>
                ) : isError ? (
                    <Box
                        sx={{
                            padding: (theme) => theme.spacing(8),
                            textAlign: "center",
                            color: (theme) => theme.palette.text.secondary,
                        }}
                    >
                        <Typography variant="body1" color="error">
                            Error loading assignment changes
                        </Typography>
                    </Box>
                ) : !changeLogs || changeLogs.length === 0 ? (
                    <Box
                        sx={{
                            padding: (theme) => theme.spacing(8),
                            textAlign: "center",
                            color: (theme) => theme.palette.text.secondary,
                        }}
                    >
                        <Typography variant="body1" color="text.secondary">
                            No assignment changes recorded yet
                        </Typography>
                    </Box>
                ) : (
                    <TableContainer
                        sx={{
                            maxHeight: 450,
                            "& .MuiTableCell-head": {
                                backgroundColor: (theme) => theme.palette.grey[100],
                                fontWeight: 600,
                                position: "sticky",
                                top: 0,
                                zIndex: 1,
                            },
                        }}
                    >
                        <Table stickyHeader>
                            <TableHead>
                                <TableRow>
                                    <TableCell
                                        sx={{
                                            fontSize: "0.875rem",
                                            fontWeight: 600,
                                            padding: (theme) => theme.spacing(1.5),
                                            backgroundColor: (theme) => theme.palette.grey[100],
                                        }}
                                    >
                                        Date & Time
                                    </TableCell>
                                    <TableCell
                                        sx={{
                                            fontSize: "0.875rem",
                                            fontWeight: 600,
                                            padding: (theme) => theme.spacing(1.5),
                                            backgroundColor: (theme) => theme.palette.grey[100],
                                        }}
                                    >
                                        Field
                                    </TableCell>
                                    <TableCell
                                        sx={{
                                            fontSize: "0.875rem",
                                            fontWeight: 600,
                                            padding: (theme) => theme.spacing(1.5),
                                            backgroundColor: (theme) => theme.palette.grey[100],
                                        }}
                                    >
                                        Previous Value
                                    </TableCell>
                                    <TableCell
                                        sx={{
                                            fontSize: "0.875rem",
                                            fontWeight: 600,
                                            padding: (theme) => theme.spacing(1.5),
                                            backgroundColor: (theme) => theme.palette.grey[100],
                                        }}
                                    >
                                        New Value
                                    </TableCell>
                                    <TableCell
                                        sx={{
                                            fontSize: "0.875rem",
                                            fontWeight: 600,
                                            padding: (theme) => theme.spacing(1.5),
                                            backgroundColor: (theme) => theme.palette.grey[100],
                                        }}
                                    >
                                        Changed By
                                    </TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {changeLogs.map((log) => (
                                    <TableRow key={log.id} hover>
                                        <TableCell
                                            sx={{
                                                fontSize: "0.875rem",
                                                padding: (theme) => theme.spacing(1.5),
                                            }}
                                        >
                                            {new Date(log.createdAt).toLocaleString("en-US", {
                                                month: "short",
                                                day: "numeric",
                                                year: "numeric",
                                                hour: "numeric",
                                                minute: "2-digit",
                                                hour12: true,
                                            })}
                                        </TableCell>
                                        <TableCell
                                            sx={{
                                                fontSize: "0.875rem",
                                                padding: (theme) => theme.spacing(1.5),
                                            }}
                                        >
                                            {log.fieldDisplayName}
                                        </TableCell>
                                        <TableCell
                                            sx={{
                                                fontSize: "0.875rem",
                                                padding: (theme) => theme.spacing(1.5),
                                            }}
                                        >
                                            <Typography
                                                variant="body2"
                                                color={
                                                    log.previousValue
                                                        ? "text.primary"
                                                        : "text.secondary"
                                                }
                                            >
                                                {log.previousValue || "None"}
                                            </Typography>
                                        </TableCell>
                                        <TableCell
                                            sx={{
                                                fontSize: "0.875rem",
                                                padding: (theme) => theme.spacing(1.5),
                                            }}
                                        >
                                            {log.newValue}
                                        </TableCell>
                                        <TableCell
                                            sx={{
                                                fontSize: "0.875rem",
                                                padding: (theme) => theme.spacing(1.5),
                                            }}
                                        >
                                            {log.changedBy}
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}
            </DialogContent>

            <DialogActions sx={{ px: 3, py: 2 }}>
                <Button onClick={onClose}>Close</Button>
                <Button
                    variant="contained"
                    startIcon={<Download size={16} />}
                    onClick={handleExportCSV}
                    disabled={!changeLogs || changeLogs.length === 0}
                >
                    Export CSV
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default AssignmentChangeLogModal;
